import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { openMetricoolUrl } from '@/utils/metricoolNavigation';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { notifyOnboardingWebhook } from '@/services/data/onboardingWebhook';
import { ONBOARDING_WEBHOOK_EVENTS } from '@/constants/onboarding';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import { getConnectionLink } from '@/services/data/socialConnections';
import { getUserById } from '@/services/data/users';
import { useOnboardingSteps } from '@/hooks/useOnboardingSteps';
import { usePreloadOnboardingImages } from '@/hooks/usePreloadOnboardingImages';
import { usePreloadOnboardingData } from '@/hooks/usePreloadOnboardingData';
import { OnboardingStepWrapper } from './OnboardingStepWrapper';

// Onboarding step illustrations
import knowledgeBaseIllustration from '@/assets/onboarding/step-1-knowledge-base.jpg';
import shootingSetupIllustration from '@/assets/onboarding/step-2-shooting-setup.jpg';
import thumbnailStyleIllustration from '@/assets/onboarding/step-4-thumbnail-style.jpg';
import networkConnectionIllustration from '@/assets/onboarding/step-5-network-connection.png';
import editorialPlanningIllustration from '@/assets/onboarding/step-6-editorial-planning.jpg';
import crossPostIllustration from '@/assets/crosspost.png';
import { KnowledgeBaseStep } from './steps/KnowledgeBaseStep';
import { ShootingSetupStep, type ShootingSetupStepRef } from './steps/ShootingSetupStep';
import { DecorSelectionStep, type DecorSelectionStepRef } from './steps/DecorSelectionStep';
import { DecorPreview } from './steps/DecorPreview';
import { ThumbnailSelectionStep, type ThumbnailSelectionStepRef } from './steps/ThumbnailSelectionStep';
import type { ThumbnailStyle } from '@/services/data/shootingPreferences';
import boldOverlay from '@/assets/thumbnails/bold-overlay.png';
import classiqueOverlay from '@/assets/thumbnails/classique-overlay.png';
import { NetworkConnectionStep } from './steps/NetworkConnectionStep';
import { EditorialPlanningStep } from './steps/EditorialPlanningStep';
import { CrossPostStep } from './steps/CrossPostStep';
import { TeamIntroductionStep } from './steps/TeamIntroductionStep';
import { TeamAvatarsPanel } from './steps/TeamAvatarsPanel';
import { DescriptionPreferencesStep, type DescriptionPreferencesStepRef } from './steps/DescriptionPreferencesStep';
import { DescriptionPreview } from './steps/DescriptionPreview';
import { ConnectionLinkDialog } from './ConnectionLinkDialog';
import type { ToneOfVoice } from '@/types/scheduling';

// Ref type for KnowledgeBaseStep
export interface KnowledgeBaseStepRef {
  openWizard: () => void;
}

interface OnboardingFlowProps {
  onComplete?: () => void;
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  // Preload all onboarding images on mount
  usePreloadOnboardingImages();
  
  // Preload all onboarding data in parallel
  const { isLoading: isDataLoading, isLoaded: isDataLoaded } = usePreloadOnboardingData();

  // Subscribe so CTA label updates when the prefetch succeeds/fails
  const connectionLinkState = useOnboardingDataStore((state) => state.connectionLinkState);

  const { userId } = useAuthStore();
  const {
    currentStep,
    currentStepIndex,
    isLoading,
    shouldShowOnboarding,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    skipOnboarding,
    progress,
    isFirstStep,
    isLastStep,
  } = useOnboardingSteps();

  useEffect(() => {
    if (!userId || !currentStep || !shouldShowOnboarding) return;
    notifyOnboardingWebhook(userId, {
      step_key: currentStep.step_key,
      step_index: currentStepIndex,
      event: ONBOARDING_WEBHOOK_EVENTS.STEP_STARTED,
    });
  }, [userId, currentStep?.step_key, currentStepIndex, shouldShowOnboarding]);

  const [isStepLoading, setIsStepLoading] = useState(false);
  const [canProceed, setCanProceed] = useState(true);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [decorColor, setDecorColor] = useState('#795AA8'); // Track decor color for left panel
  const [selectedThumbnailStyle, setSelectedThumbnailStyle] = useState<ThumbnailStyle | null>(null);
  const [customFontUrl, setCustomFontUrl] = useState<string | null>(null);
  const [customFontLoaded, setCustomFontLoaded] = useState(false);
  // Description preferences state for live preview
  const [descriptionPreviewState, setDescriptionPreviewState] = useState({
    toneOfVoice: 'vous' as ToneOfVoice,
    hasRecurringCta: false,
    recurringCtaText: '',
    hasEmojis: true,
  });
  // Dialog state for connection link fallback
  const [showConnectionLinkDialog, setShowConnectionLinkDialog] = useState(false);
  const knowledgeBaseRef = useRef<KnowledgeBaseStepRef>(null);
  const shootingSetupRef = useRef<ShootingSetupStepRef>(null);
  const decorSelectionRef = useRef<DecorSelectionStepRef>(null);
  const thumbnailSelectionRef = useRef<ThumbnailSelectionStepRef>(null);
  const descriptionPreferencesRef = useRef<DescriptionPreferencesStepRef>(null);

  const handleDecorColorChange = useCallback((color: string) => {
    setDecorColor(color);
  }, []);

  const handleThumbnailStyleChange = useCallback((style: ThumbnailStyle | null) => {
    setSelectedThumbnailStyle(style);
  }, []);

  const handleCustomFontChange = useCallback((fontUrl: string | null) => {
    setCustomFontUrl(fontUrl);
    setCustomFontLoaded(false);
  }, []);

  const handleDescriptionPreferencesChange = useCallback((preferences: {
    toneOfVoice: ToneOfVoice;
    hasRecurringCta: boolean;
    recurringCtaText: string;
    hasEmojis: boolean;
  }) => {
    setDescriptionPreviewState(preferences);
  }, []);

  // Load custom font dynamically when URL changes
  useEffect(() => {
    const fontName = 'CustomOnboardingFont';
    
    // Remove any existing custom font first
    const existingFonts = Array.from(document.fonts).filter(
      (font) => font.family === fontName
    );
    existingFonts.forEach((font) => document.fonts.delete(font));
    
    if (!customFontUrl) {
      setCustomFontLoaded(false);
      return;
    }

    setCustomFontLoaded(false);
    
    const fontFace = new FontFace(fontName, `url(${customFontUrl})`);
    
    fontFace.load().then((loadedFont) => {
      document.fonts.add(loadedFont);
      setCustomFontLoaded(true);
    }).catch((error) => {
      console.error('Error loading custom font:', error);
      setCustomFontLoaded(false);
    });
  }, [customFontUrl]);

  const handleCanProceedChange = useCallback((value: boolean) => {
    setCanProceed(value);
  }, []);
  
  const handleWizardOpenChange = useCallback((isOpen: boolean) => {
    setIsWizardOpen(isOpen);
  }, []);

  // Reset canProceed when step changes - each step manages its own state
  // Steps that don't need validation (knowledge_base, network_connection, etc.) should allow proceeding
  const stepRequiresValidation = currentStep?.step_key === 'shooting_setup' || currentStep?.step_key === 'thumbnail_selection';
  const effectiveCanProceed = stepRequiresValidation ? canProceed : true;

  // Get custom left panel for current step (takes precedence over illustration)
  const customLeftPanel = useMemo(() => {
    if (currentStep?.step_key === 'decor_selection') {
      return <DecorPreview color={decorColor} />;
    }
    if (currentStep?.step_key === 'team_introduction') {
      return <TeamAvatarsPanel />;
    }
    if (currentStep?.step_key === 'thumbnail_selection') {
      return (
        <div className="relative w-full h-full">
          <img 
            src={thumbnailStyleIllustration} 
            alt="Illustration" 
            className="w-full h-full object-cover object-[center_30%]"
          />
          {selectedThumbnailStyle === 'bold' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <img 
                src={boldOverlay} 
                alt="Bold Overlay" 
                className="w-[85%] h-full object-contain transition-opacity duration-300"
              />
            </div>
          )}
          {selectedThumbnailStyle === 'classique' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none -translate-y-[8px]">
              <img 
                src={classiqueOverlay} 
                alt="Classique Overlay" 
                className="w-[85%] h-full object-contain transition-opacity duration-300"
              />
            </div>
          )}
          {selectedThumbnailStyle === 'custom' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center px-8">
                <p 
                  className="text-7xl font-bold text-white/90 leading-none tracking-tight drop-shadow-lg transition-all duration-300"
                  style={customFontLoaded && customFontUrl ? { fontFamily: 'CustomOnboardingFont, sans-serif' } : undefined}
                >
                  Your<br />Custom<br />Type
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }
    if (currentStep?.step_key === 'description_preferences') {
      return (
        <DescriptionPreview
          toneOfVoice={descriptionPreviewState.toneOfVoice}
          hasRecurringCta={descriptionPreviewState.hasRecurringCta}
          recurringCtaText={descriptionPreviewState.recurringCtaText}
          hasEmojis={descriptionPreviewState.hasEmojis}
        />
      );
    }
    return undefined;
  }, [currentStep?.step_key, decorColor, selectedThumbnailStyle, customFontLoaded, customFontUrl, descriptionPreviewState]);

  // Don't render if not needed or still loading data
  // Wait for both onboarding steps config AND preloaded data to be ready
  if (!shouldShowOnboarding || isLoading || !isDataLoaded) {
    if (isLoading || isDataLoading || !isDataLoaded) {
      return createPortal(
        <div className="fixed inset-0 bg-black flex items-center justify-center z-[60]">
          <Loader2 className="h-8 w-8 text-white/60 animate-spin" />
        </div>,
        document.body
      );
    }
    return null;
  }

  if (!currentStep) return null;

  const handleNext = async () => {
    if (userId && currentStep) {
      notifyOnboardingWebhook(userId, {
        step_key: currentStep.step_key,
        step_index: currentStepIndex,
        event: ONBOARDING_WEBHOOK_EVENTS.STEP_COMPLETED,
      });
    }
    goToNextStep();
    if (isLastStep && onComplete) {
      onComplete();
    }
  };

  const handleSkip = async () => {
    await skipOnboarding();
    if (onComplete) {
      onComplete();
    }
  };

  const getStepContent = () => {
    switch (currentStep.step_key) {
      case 'knowledge_base':
        return (
          <KnowledgeBaseStep 
            ref={knowledgeBaseRef}
            onComplete={handleNext}
            setLoading={setIsStepLoading}
            onWizardOpenChange={handleWizardOpenChange}
          />
        );
      case 'shooting_setup':
        return (
          <ShootingSetupStep 
            ref={shootingSetupRef}
            onComplete={handleNext}
            setLoading={setIsStepLoading}
            onCanProceedChange={handleCanProceedChange}
          />
        );
      case 'decor_selection':
        return (
          <DecorSelectionStep 
            ref={decorSelectionRef}
            onComplete={handleNext}
            setLoading={setIsStepLoading}
            onCanProceedChange={handleCanProceedChange}
            onColorChange={handleDecorColorChange}
          />
        );
      case 'thumbnail_selection':
        return (
          <ThumbnailSelectionStep 
            ref={thumbnailSelectionRef}
            onComplete={handleNext}
            setLoading={setIsStepLoading}
            onCanProceedChange={handleCanProceedChange}
            onStyleChange={handleThumbnailStyleChange}
            onFontChange={handleCustomFontChange}
          />
        );
      case 'description_preferences':
        return (
          <DescriptionPreferencesStep 
            ref={descriptionPreferencesRef}
            onComplete={handleNext}
            setLoading={setIsStepLoading}
            onCanProceedChange={handleCanProceedChange}
            onPreferencesChange={handleDescriptionPreferencesChange}
          />
        );
      case 'network_connection':
        return (
          <NetworkConnectionStep 
            onComplete={handleNext}
            setLoading={setIsStepLoading}
          />
        );
      case 'editorial_planning':
        return (
          <EditorialPlanningStep 
            onComplete={handleNext}
            setLoading={setIsStepLoading}
          />
        );
      case 'cross_post':
        return (
          <CrossPostStep 
            onComplete={handleNext}
            setLoading={setIsStepLoading}
          />
        );
      case 'team_introduction':
        return (
          <TeamIntroductionStep 
            onComplete={handleNext}
            setLoading={setIsStepLoading}
          />
        );
      default:
        return (
          <div className="text-white/60 text-center py-8">
            Étape inconnue: {currentStep.step_key}
          </div>
        );
    }
  };

  const getStepTitle = () => {
    switch (currentStep.step_key) {
      case 'knowledge_base':
        return 'Base de connaissance';
      case 'shooting_setup':
        return 'Préparons votre tournage';
      case 'decor_selection':
        return 'Sélection du décor';
      case 'thumbnail_selection':
        return 'Style de miniature';
      case 'description_preferences':
        return 'Descriptions';
      case 'network_connection':
        return 'Diffusion et statistiques';
      case 'editorial_planning':
        return 'Planning éditorial';
      case 'cross_post':
        return 'Cross-post Instagram';
      case 'team_introduction':
        return 'Votre équipe';
      default:
        return currentStep.display_name;
    }
  };

  // Dynamic subtitle based on step
  const getStepSubtitle = () => {
    if (currentStep.step_key === 'knowledge_base') {
      return undefined; // Title is inside the step component
    }
    if (currentStep.step_key === 'shooting_setup') {
      return 'Quelle posture souhaitez-vous adopter ?';
    }
    if (currentStep.step_key === 'decor_selection') {
      return 'Choisis la couleur de ton décor';
    }
    if (currentStep.step_key === 'thumbnail_selection') {
      return 'Quel style de miniature préférez-vous ?';
    }
    if (currentStep.step_key === 'description_preferences') {
      return 'Quel est votre ton de voix ?';
    }
    if (currentStep.step_key === 'team_introduction') {
      return 'Les personnes qui vont t\'accompagner';
    }
    return currentStep.description || undefined;
  };

  // Custom CTA handler based on step type
  const handleCTAClick = async () => {
    console.log('[Onboarding] CTA clicked, step:', currentStep.step_key);

    const stepRefHandlers: Record<string, () => void> = {
      knowledge_base: () => knowledgeBaseRef.current?.openWizard(),
      shooting_setup: () => shootingSetupRef.current?.handleContinue(),
      decor_selection: () => decorSelectionRef.current?.handleContinue(),
      thumbnail_selection: () => thumbnailSelectionRef.current?.handleContinue(),
      description_preferences: () => descriptionPreferencesRef.current?.handleContinue(),
    };

    const refHandler = stepRefHandlers[currentStep.step_key];
    if (refHandler) {
      refHandler();
      return;
    }

    if (currentStep.step_key === 'network_connection') {
      if (connectionLinkState.status === 'success' && connectionLinkState.link) {
        try {
          openMetricoolUrl(connectionLinkState.link);
          toast.info('Connexion en cours', {
            description: 'Connectez vos réseaux dans le navigateur. Revenez ici après.',
          });
        } catch (err) {
          console.error('[Onboarding] Error opening prefetched link:', err);
          toast.error('Erreur', { description: "Impossible d'ouvrir le lien" });
        }
        return;
      }

      if (connectionLinkState.status === 'error') {
        setShowConnectionLinkDialog(true);
        return;
      }

      const userId = useAuthStore.getState().userId;
      if (!userId) {
        toast.error('Erreur', { description: 'Utilisateur non connecté' });
        return;
      }

      setIsStepLoading(true);
      try {
        const userResult = await getUserById(userId);
        if (!userResult.success || !userResult.data?.metricool_blog_id) {
          toast.error('Erreur', { description: 'Compte non configuré avec Metricool' });
          return;
        }

        const blogId = userResult.data.metricool_blog_id;
        const email = userResult.data.email ?? undefined;

        try {
          const result = await getConnectionLink(userId, blogId, { email });

          if (result.success && result.link) {
            useOnboardingDataStore.getState().setConnectionLinkSuccess(result.link);
            openMetricoolUrl(result.link);
            toast.info('Connexion en cours', {
              description: 'Connectez vos réseaux dans le navigateur. Revenez ici après.',
            });
          } else {
            const errCode = result.reason === 'token_expired' ? 'TOKEN_EXPIRED' : (result.error || 'Erreur inconnue');
            useOnboardingDataStore.getState().setConnectionLinkError(errCode);
            setShowConnectionLinkDialog(true);
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.error('[Onboarding] Connection link request failed:', err);
          useOnboardingDataStore.getState().setConnectionLinkError(msg);
          setShowConnectionLinkDialog(true);
        }
      } finally {
        setIsStepLoading(false);
      }
      return;
    }

    handleNext();
  };

  // Custom CTA label based on step
  const getNextLabel = () => {
    if (currentStep.step_key === 'knowledge_base') {
      return 'Compléter';
    }
    if (currentStep.step_key === 'network_connection') {
      // If we already know the connection link failed, switch CTA label
      if (connectionLinkState.status === 'error') {
        return 'Recevoir mon lien';
      }
      return 'Connecter mes réseaux';
    }
    return isLastStep ? 'Terminer' : 'Continuer';
  };

  // Get illustration for current step
  const getStepIllustration = () => {
    switch (currentStep.step_key) {
      case 'knowledge_base':
        return knowledgeBaseIllustration;
      case 'shooting_setup':
        return shootingSetupIllustration;
      case 'thumbnail_selection':
        return thumbnailStyleIllustration;
      case 'description_preferences':
        return knowledgeBaseIllustration; // Reuse knowledge base illustration for now
      case 'network_connection':
        return networkConnectionIllustration;
      case 'editorial_planning':
        return editorialPlanningIllustration;
      case 'cross_post':
        return crossPostIllustration;
      case 'team_introduction':
        return undefined; // No illustration, content fills the right panel
      default:
        return undefined;
    }
  };


  const content = (
    <OnboardingStepWrapper
      title={getStepTitle()}
      subtitle={getStepSubtitle()}
      progress={progress}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      isLoading={isStepLoading}
      canProceed={effectiveCanProceed}
      onBack={goToPreviousStep}
      onNext={handleCTAClick}
      onSkip={handleSkip}
      onSkipStep={goToNextStep}
      onGoToStep={goToStep}
      nextLabel={getNextLabel()}
      illustrationSrc={getStepIllustration()}
      customLeftPanel={customLeftPanel}
      hideFooterButton={isWizardOpen}
      showLeftPanelBorder={currentStep.step_key === 'description_preferences'}
    >
      {getStepContent()}
    </OnboardingStepWrapper>
  );

  return createPortal(
    <>
      {content}
      <ConnectionLinkDialog
        open={showConnectionLinkDialog}
        onClose={() => setShowConnectionLinkDialog(false)}
        onContinue={handleNext}
      />
    </>,
    document.body
  );
}
